/**
 * Keeps the theme's cart drawer + header cart icon bubble in sync with
 * third-party app widgets (e.g. Cowlendar, Reputon) that add to cart via
 * their own fetch/XHR calls to Shopify's cart endpoints, bypassing the
 * theme's own product-form.js flow.
 *
 * Detects any successful request to /cart/add, /cart/change, or /cart/update
 * (with or without the .js suffix) and asks Shopify for a fresh render of
 * the cart-drawer + cart-icon-bubble sections via the same "sections"
 * parameter product-form.js uses on a normal add-to-cart, then hands the
 * result to cart-drawer.js's own renderContents() — the exact method that
 * already renders the drawer correctly, empty-state and all.
 */
(() => {
  if (window.__cartSyncInstalled) return;
  window.__cartSyncInstalled = true;

  const CART_ENDPOINT_PATTERN = /\/cart\/(add|change|update)(\.js)?(\?|$)/;

  // Captured before window.fetch is patched below, so our own internal
  // refresh call never re-triggers the interceptor and loops on itself.
  const originalFetch = window.fetch;

  function refreshCartUI() {
    if (typeof routes === 'undefined' || !routes.cart_update_url) return Promise.resolve();

    const cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl || typeof cartDrawerEl.renderContents !== 'function') return Promise.resolve();

    const sections = cartDrawerEl.getSectionsToRender().map((section) => section.id);

    return originalFetch(routes.cart_update_url, {
      ...fetchConfig(),
      ...{ body: JSON.stringify({ sections, sections_url: window.location.pathname }) },
    })
      .then((response) => response.json())
      .then((state) => {
        cartDrawerEl.renderContents(state);
        // renderContents() only clears is-empty from .drawer__inner, but the
        // layout CSS (padding, header visibility) is keyed off is-empty on
        // <cart-drawer> itself — product-form.js handles that separately for
        // its own native add-to-cart flow, so we need to do the same here.
        cartDrawerEl.classList.toggle('is-empty', state.item_count === 0);
      })
      .catch((e) => console.error('[cart-sync]', e));
  }

  const handleCartMutation = debounce(refreshCartUI, 150);

  // Patch fetch — most embedded app widgets add to cart this way.
  window.fetch = function (...args) {
    const request = args[0];
    const url = typeof request === 'string' ? request : request && request.url;
    const isCartMutation = url && CART_ENDPOINT_PATTERN.test(url);

    return originalFetch.apply(this, args).then((response) => {
      if (isCartMutation && response.ok) handleCartMutation();
      return response;
    });
  };

  // Patch XMLHttpRequest too — some app widgets still use jQuery.ajax/XHR.
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__isCartMutation = typeof url === 'string' && CART_ENDPOINT_PATTERN.test(url);
    return originalOpen.call(this, method, url, ...rest);
  };

  const originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__isCartMutation) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) handleCartMutation();
      });
    }
    return originalSend.apply(this, args);
  };
})();
